/**
 * The on-screen action controls: split, eject and fire, in the bottom-right corner.
 *
 * There used to be a second arrangement — `ActionFooter`, the same controls plus Leave in a strip
 * *below* the arena — because a phone held in **portrait** put the overlay exactly where a thumb
 * steering down the map ended up, and a press meant as "swim this way" fired a split instead.
 * **It is gone, and the landscape lock is why.** The shell no longer opens in portrait at all
 * (`AndroidManifest.xml`, `ios/App/App/Info.plist`) and a phone browser held upright gets the
 * rotate interstitial over the whole arena (`rotate-prompt.ts`), so the only viewers left for a
 * portrait-shaped layout were a tablet held upright and a desktop window dragged narrow —
 * neither of which has the thumb the strip was invented for. Keeping it would have meant
 * maintaining a second arena layout, and a second place for Leave, for nobody. The corner is the
 * one arrangement now, and Leave is back in the HUD's own top-right corner for good.
 *
 * `ActionButton` is deliberately generic, which is what the power-up control then cost: one more
 * `<ActionButton>` in the same row, with its own label and its own `onPress`, and no third
 * pattern. Nothing about split, eject or firing is baked into the button itself — which is also
 * how the sayings picker gets into the row without this file learning that sayings exist: it is
 * a `sayings` **node**, built by the route because it reads tRPC, and dropped in beside the rest.
 *
 * The inventory is the same bargain paid a third time. A player carries several power-ups now, so
 * the corner grew a row of **slots** — one `ActionButton` each, wearing a glyph and its own
 * remaining seconds or charges — rather than a second kind of control with its own pointer rules.
 * It sits *above* the round row and never beside it; see the note at the call site for why the
 * one direction this corner may grow in is up.
 *
 * Four rules, and every one of them is about not fighting the game underneath:
 *
 * - **They never occlude play.** Low-alpha fill over a blur, exactly as every other HUD panel
 *   (`HUD_PANEL_CLASS`), in the one corner the minimap and the leaderboard do not use — a player
 *   has to be able to see the dot they are about to eat through whatever is drawn over it.
 * - **They never steer.** The overlay is `pointer-events-none` and only these opt back in, and a
 *   press is taken on `pointerdown` with the default prevented, so the touch that fires a split
 *   is not also a touch the canvas reads as "steer here". `touch-action: none` is the other half:
 *   without it a thumb that slides a few pixels while pressing becomes a page scroll. (What
 *   actually keeps a press off the canvas is `pointerSteers`; see `ActionButtonTrigger`, which is
 *   why the one control that *opens* something can safely leave the default alone.)
 * - **They repeat rather than debounce.** Holding one is the 2 -> 4 -> 8 cascade — `HoldRepeat`
 *   on the same delay and the same TICK_MS interval the keyboard uses, so the two controls are
 *   the same control. Nothing here rate-limits; `GameConnection` is what decides that a press
 *   rides one input frame and only one.
 * - **They answer immediately.** The pressed state is local and instant. What the press *did* is
 *   the server's to say, a round trip later, and the arena draws that separately (see
 *   `ACTION_CUE_MS` in `ArenaScene`); a button that waited for the tick to come back would feel
 *   broken at any ping at all.
 */

import { type ReactNode, type Ref, useCallback, useEffect, useRef, useState } from 'react';
import { HOLD_REPEAT_INTERVAL_MS, HoldRepeat, sprintControl } from '../input/actions';
import type { InventorySlotView } from './inventory-strip';

/**
 * Whether to draw the on-screen controls at all.
 *
 * Two conditions, because neither alone is the question. A coarse pointer is a thumb and wants
 * them; a narrow window is a phone held in portrait whether or not the browser admits to a
 * touchscreen, and it is also what a desktop browser's device emulation reports. A desktop user
 * who drags their window narrow gets two buttons they can also click, which costs them nothing.
 */
export const TOUCH_CONTROLS_QUERY = '(pointer: coarse), (max-width: 639px)';

export function useTouchControls(): boolean {
  return useMediaQuery(TOUCH_CONTROLS_QUERY);
}

/**
 * A media query as a boolean that re-renders when it changes.
 *
 * Exported because the rotate interstitial reads the same primitives these two do — a coarse
 * pointer and a narrow viewport — and a second implementation of "watch a media query" is how
 * one of them ends up not listening for changes.
 */
export function useMediaQuery(query: string): boolean {
  const [enabled, setEnabled] = useState(() => matchesQuery(query));

  useEffect(() => {
    // jsdom has no `matchMedia`, and neither does a server render.
    if (typeof window.matchMedia !== 'function') return;
    const list = window.matchMedia(query);
    const onChange = () => setEnabled(list.matches);
    onChange();
    list.addEventListener('change', onChange);
    return () => list.removeEventListener('change', onChange);
  }, [query]);

  return enabled;
}

function matchesQuery(query: string): boolean {
  if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return false;
  return window.matchMedia(query).matches;
}

export type ActionButtonTone = 'neutral' | 'warn' | 'power';

/**
 * When a press counts.
 *
 * - **`press`** is the game's rule and the default: the edge is taken on `pointerdown`, because a
 *   split has a 20 Hz deadline and a click is only reported when the finger comes back *up*. It
 *   is load-bearing for split, eject and fire — see "Reaching split and eject" in CLAUDE.md — and
 *   nothing here may quietly move them off it.
 * - **`tap`** is for a control that *opens* something rather than doing something. There is no
 *   deadline on a menu, and taking one on `pointerdown` is actively wrong: the panel appears
 *   under the finger that is still down, and the rest of that same tap then lands on the panel
 *   and dismisses it again. Holding the finger there defers the dismissal, which is exactly the
 *   "I have to push and hold Say" the player reported. A tap control fires on the gesture
 *   *completing*, so the whole gesture belongs to the button.
 *
 * **AND "COMPLETING" IS `pointerup`, NOT `click` — WHICH IS THE WHOLE OF WHY SAY WAS DEAD UNDER A
 * SECOND FINGER.** The player: "I held joystick with thumb and tried pressing say over and over
 * but nothing until I lift my thumb and then press."
 *
 * A `click` on a touch screen is not dispatched from the pointer stream; it is dispatched from a
 * **tap gesture** the platform's gesture recogniser has decided happened. With a thumb already
 * down on the stick, a second finger is a second contact in a multi-finger gesture, and what the
 * recogniser reports for it is not a tap — so no click arrives, however many times it is pressed.
 * Lift the thumb and the next contact is a lone one, the recogniser calls it a tap, and the
 * button works: exactly the report, in both of its halves.
 *
 * The specification is *not* the thing that predicts this, and it is worth writing down which way
 * round that is. `click` is deliberately not a compatibility mouse event, and the spec says user
 * agents typically fire it for non-primary pointers too — so "the second finger is not primary"
 * is the wrong explanation, even though it produces the right prediction here. What is actually
 * true is narrower: whether a click arrives at all is a *gesture* decision on this platform, and a
 * control that must work while another finger is down cannot be built on one. `pointerdown` and
 * `pointerup` are dispatched per pointer with no gesture in the way, which is why the three
 * `press` controls beside this one were never affected.
 *
 * So `tap` moves to `pointerup`. It still lands at the end of the gesture — the reason `tap`
 * exists — and no longer depends on anything deciding what kind of gesture that was.
 *
 * `onClick` therefore survives for the **keyboard alone** (`detail === 0`), for both triggers —
 * Enter or Space on a focused button is the one activation no pointer event accompanies.
 *
 * A `tap` control also does not `preventDefault` the pointer down. That used to be about
 * protecting the click and is now about the platform's own affordances (a long-press, a
 * selection), and it was never what kept the press off the canvas anyway: `pointerSteers` in
 * `scenes/steering.ts` is, because Phaser reads its own `touchstart` on **window** and a
 * `preventDefault` on React's `pointerdown` never closed that.
 *
 * ── AND A THIRD ONE, WHICH IS NEITHER: `hold` ──
 *
 * **`hold`** is for a control whose meaning is "while this is down" rather than "this happened".
 * The sprint is the only one, and it is the first control in this client that is not an edge at
 * all (see the section at the top of `../input/actions.ts`). It fires `onPress` on the way down
 * and `onRelease` on the way up, and it never repeats: there is nothing to repeat, because what
 * the wire carries is a level rather than a count.
 *
 * **A STRANDED HOLD IS THE FAILURE THIS SHAPE IS DESIGNED AGAINST.** A sprint left on by a
 * gesture nobody finished burns a bar the player stopped spending, so the release has to be
 * reachable by *every* path a press can end by — the finger lifting, the browser cancelling the
 * pointer (a scroll or a system gesture taking it over), the capture being lost, and the control
 * unmounting. `setPointerCapture` is what makes the first of those reliable: without it a thumb
 * that slides off the button never delivers its `pointerup` here at all, and with it the browser
 * fires `lostpointercapture` even when it takes the pointer away for reasons of its own.
 * `pointerLeave` is deliberately *not* one of them, unlike a `press` control's: with the pointer
 * captured a thumb that strays a few pixels is still holding the button, and stopping the sprint
 * because a finger wandered would be the control letting go on its own.
 *
 * **The keyboard is deliberately not a path into it.** A synthesised click (`detail === 0`) is an
 * instant, and "while held" cannot be expressed by one — firing press-and-release would be a
 * zero-length sprint and firing press alone would be the stranded hold above. The accessible
 * equal is the real binding, which is on the keyboard already: `ARENA_KEY_BINDINGS.sprint`. That
 * is the thumb stick's own argument (a pointer-only control whose keyboard equivalent is the
 * arrow cross) applied to a button rather than to a pad.
 */
export type ActionButtonTrigger = 'press' | 'tap' | 'hold';

export interface ActionButtonProps {
  /** The word on the button. Two or three letters read at a glance; a sentence does not. */
  label: string;
  /** What a screen reader (and a long-press tooltip) is told. */
  description: string;
  /** The desktop binding, printed under the label so the two controls teach each other. */
  binding?: string;
  /** Colour, as a Tailwind class pair. The default is the neutral panel treatment. */
  tone?: ActionButtonTone;
  /**
   * Greyed out and inert. Used by the power-up control, which has nothing to fire until the
   * player is carrying something — a button that always looked live would be a button whose
   * silence read as a dropped input rather than as an empty slot.
   */
  disabled?: boolean;
  /**
   * How big a target it is. `lg` is the split on a screen with room for it; `sm` is what a short
   * landscape phone gets, which trades the generous target for a corner that costs a 390-pixel
   * screen as little as possible — and is still 48px, comfortably past the 44px a thumb needs.
   *
   * `xs` is smaller than that minimum and is used for exactly one thing: an inventory slot on a
   * *compact* surface. The row of them grows **up** the right rail rather than left into the
   * steering zone, so every pixel of height it spends is a pixel the leaderboard above it gives
   * back — and a slot is a control a player reaches for between chases rather than under one,
   * which is the difference that makes 40px an acceptable target where it would not be for Split.
   */
  size?: 'xs' | 'sm' | 'md' | 'lg';
  /**
   * Drawn as the one that is chosen. The inventory strip's, and it is a *ring* rather than a
   * different fill: what the row has to say at a glance is which slot Q would spend, and a colour
   * change would fight the tone that already says what kind of thing a slot is.
   */
  selected?: boolean;
  /**
   * Whether holding it repeats. True for the three actions — holding split *is* the 2 -> 4 -> 8
   * cascade — and false for Leave, which happens once however long a thumb rests on it.
   * Ignored by a `tap` control, which has no press on the way down to repeat from.
   */
  repeat?: boolean;
  /** Whether the press is the finger landing or the tap completing. See `ActionButtonTrigger`. */
  trigger?: ActionButtonTrigger;
  /**
   * The button element itself, for a caller that has to position something against it.
   *
   * A ref rather than a wrapper element, because the action row is a flex row of fixed-size
   * children and a `<span>` around one of them is a layout change to buy a measurement. The one
   * caller is `SayingsPicker`, whose panel opens above whichever of the two places this button
   * is drawn in — see `hud/anchor.ts`.
   */
  buttonRef?: Ref<HTMLButtonElement>;
  /** Called once per press, and again every HOLD_REPEAT_INTERVAL_MS while held. */
  onPress: () => void;
  /**
   * Called when a `hold` control's press ends, by whichever of the four paths ended it — and on
   * unmount. Ignored by the other two triggers, which have no "still down" to end.
   *
   * It is allowed to be called when nothing was pressed (a cancel that follows a refused down, an
   * unmount with no finger on the button), because the alternative is bookkeeping that can get
   * "was it really down" wrong in the one direction that strands a sprint. `SprintControl.release`
   * is idempotent for exactly this reason.
   */
  onRelease?: () => void;
  testId?: string;
}

const TONE_CLASS: Record<ActionButtonTone, string> = {
  neutral: 'border-border/60 bg-card/30 text-foreground',
  warn: 'border-amber-400/50 bg-amber-400/15 text-amber-200',
  // The orb's own gold, so the control and the thing it fires are obviously the same mechanic.
  power: 'border-amber-300/70 bg-amber-300/25 text-amber-100',
};

const SIZE_CLASS: Record<'xs' | 'sm' | 'md' | 'lg', string> = {
  // 40px, and the only rung under the 44px minimum. See `ActionButtonProps.size`.
  xs: 'h-10 w-10 text-[10px]',
  // 48px: still past the 44px minimum touch target.
  sm: 'h-12 w-12 text-[10px]',
  md: 'h-16 w-16 text-[11px]',
  lg: 'h-20 w-20 text-xs',
};

/**
 * One round, translucent, repeat-firing control.
 *
 * Pointer events rather than `onClick`, because a click is only reported when the finger comes
 * *up* — half a second of hold later, and only once. `onClick` is kept for the keyboard path
 * alone: a browser synthesises a click with `detail === 0` when a focused button is activated by
 * Enter or Space, which is the one activation no pointer event accompanies.
 *
 * All of which is `trigger: 'press'`. A `trigger: 'tap'` control inverts it — the `pointerup` *is*
 * the press, and `pointerdown` only lights the button up — because what it does is open a panel
 * that the rest of the same gesture would otherwise dismiss. Deliberately not the `click`: see
 * `ActionButtonTrigger` for why that made the control dead under a second finger.
 */
export function ActionButton({
  label,
  description,
  binding,
  tone = 'neutral',
  size = 'md',
  selected = false,
  disabled = false,
  repeat = true,
  trigger = 'press',
  buttonRef,
  onPress,
  onRelease,
  testId,
}: ActionButtonProps) {
  const onPressRef = useRef(onPress);
  onPressRef.current = onPress;
  const onReleaseRef = useRef(onRelease);
  onReleaseRef.current = onRelease;
  // Read through a ref so `start` keeps a stable identity across the renders that flip it.
  const disabledRef = useRef(disabled);
  disabledRef.current = disabled;
  const repeatRef = useRef(repeat);
  repeatRef.current = repeat;
  const tapRef = useRef(trigger === 'tap');
  tapRef.current = trigger === 'tap';
  const holdRef = useRef(trigger === 'hold');
  holdRef.current = trigger === 'hold';
  const hold = useRef(new HoldRepeat());
  const timer = useRef<ReturnType<typeof setInterval> | null>(null);
  const [held, setHeld] = useState(false);
  /**
   * The pointer a `tap` control's gesture began with, so the `pointerup` that completes it can be
   * told from one that merely ended over this button — a finger that started on the arena, or the
   * second half of a gesture whose first half was somewhere else. Null between taps.
   */
  const tapPointer = useRef<number | null>(null);

  const fire = useCallback((presses: number) => {
    for (let i = 0; i < presses; i += 1) onPressRef.current();
  }, []);

  const release = useCallback(() => {
    hold.current.release();
    if (timer.current !== null) clearInterval(timer.current);
    timer.current = null;
    tapPointer.current = null;
    setHeld(false);
    // THE LEVEL COMES BACK DOWN HERE AND NOWHERE ELSE, which is what makes every path out of a
    // hold — the finger lifting, a cancel, a lost capture, an unmount — one path. Called
    // unconditionally rather than only when something was pressed: `onRelease` is idempotent by
    // contract, and bookkeeping that could get "was it down" wrong in the wrong direction is
    // exactly the stranded sprint this shape exists to prevent. See `ActionButtonTrigger`.
    onReleaseRef.current?.();
  }, []);

  /**
   * A pointer coming up ON this button, which for a `tap` control is the press itself.
   *
   * Separate from `release` because the two ends of a gesture are different facts: a `pointerup`
   * here is the tap landing, where a `pointerleave` or a `pointercancel` is the same gesture
   * being abandoned and must fire nothing. `press` controls took their press on the way down and
   * are unaffected either way.
   */
  const finish = useCallback(
    (event: React.PointerEvent<HTMLButtonElement>) => {
      const opened = tapPointer.current === event.pointerId;
      release();
      if (tapRef.current && opened && !disabledRef.current) onPressRef.current();
    },
    [release],
  );

  // A pointer that goes up outside the button, or a HUD that unmounts mid-hold, must not leave
  // an interval firing splits at a player who let go.
  useEffect(() => release, [release]);

  const start = useCallback(
    (event: React.PointerEvent<HTMLButtonElement>) => {
      if (holdRef.current) {
        // A LEVEL, so there is nothing to repeat and no `HoldRepeat` here: the press is the
        // control going down and the whole of what follows is it staying down. The default is
        // cancelled for a `press` control's reason (no page scroll, no long-press menu over the
        // arena), and the pointer is CAPTURED so a thumb that strays off the button still
        // delivers the `pointerup` that ends the sprint — see `ActionButtonTrigger`.
        event.preventDefault();
        if (disabledRef.current) return;
        event.currentTarget.setPointerCapture?.(event.pointerId);
        setHeld(true);
        onPressRef.current();
        return;
      }
      if (tapRef.current) {
        // The gesture is not finished, so nothing has been pressed yet — this is only the button
        // lighting up under the finger, and the pointer being remembered so its `pointerup` can
        // be recognised. The default is deliberately left alone, and nothing steers either way —
        // see `ActionButtonTrigger`.
        if (disabledRef.current) return;
        tapPointer.current = event.pointerId;
        setHeld(true);
        return;
      }
      // Stops the press from also reaching the canvas as a steer, and stops a long press from
      // opening the platform's text-selection or context menu on top of the arena.
      event.preventDefault();
      if (disabledRef.current || timer.current !== null) return;
      setHeld(true);
      // The press is this event's; the interval only ever adds the repeats.
      onPressRef.current();
      if (!repeatRef.current) return;
      hold.current.begin(performance.now());
      timer.current = setInterval(() => {
        fire(hold.current.repeat(performance.now()));
      }, HOLD_REPEAT_INTERVAL_MS);
    },
    [fire],
  );

  return (
    <button
      type="button"
      ref={buttonRef}
      data-testid={testId}
      aria-label={description}
      aria-pressed={selected ? true : undefined}
      title={description}
      disabled={disabled}
      className={`pointer-events-auto flex touch-none select-none flex-col items-center justify-center rounded-full border font-bold uppercase tracking-wider shadow-lg backdrop-blur-sm transition-transform ${TONE_CLASS[tone]} ${SIZE_CLASS[size]} ${held ? 'scale-90 brightness-150' : ''} ${disabled ? 'opacity-40' : ''} ${selected ? 'ring-2 ring-amber-200 ring-offset-1 ring-offset-transparent' : ''}`}
      onPointerDown={start}
      onPointerUp={finish}
      onPointerCancel={release}
      // A HOLD KEEPS THE BUTTON WHILE THE FINGER STRAYS, and that is the one place it parts
      // company with a `press` control. The pointer is captured, so a thumb a few pixels off the
      // edge is still holding it; releasing on `pointerleave` would be the control letting go on
      // its own mid-sprint. Every real way out is still covered — up, cancel, lost capture,
      // unmount.
      onPointerLeave={trigger === 'hold' ? undefined : release}
      // The browser taking the pointer away for reasons of its own — a system gesture, a scroll
      // it decided to own. It is the path a `pointerup` never arrives on, and therefore the one a
      // stranded sprint would have come through.
      onLostPointerCapture={trigger === 'hold' ? release : undefined}
      onContextMenu={(event) => event.preventDefault()}
      onClick={(event) => {
        if (disabled) return;
        // A `tap` control takes its press here: the whole gesture has landed on this button, so
        // whatever it opens cannot be dismissed by the tail of the tap that opened it. For a
        // `press` control this is the keyboard path alone — a browser synthesises `detail === 0`
        // for Enter/Space, and every pointer press was already taken on `pointerdown`.
        // The KEYBOARD path only, for both triggers. A pointer's press has already been taken —
        // on the way down for `press`, on `pointerup` for `tap` — and a synthesised click that
        // also fired would be that press counted twice. `detail === 0` is what a browser reports
        // for Enter or Space on a focused button, and it is the one activation no pointer event
        // accompanies. It is also why a tap must never be keyed off this event: a click is a
        // dispatched from a tap *gesture*, which the platform does not report while another
        // finger is down.
        // A HOLD IS NOT REACHABLE FROM HERE, deliberately: "while held" cannot be expressed by a
        // click, and either half of trying — a press with no release, or a press and release in
        // the same instant — is worse than not offering it. The accessible equal is the real
        // binding on the keyboard. See `ActionButtonTrigger`.
        if (event.detail === 0 && trigger !== 'hold') onPressRef.current();
      }}
    >
      <span>{label}</span>
      {binding !== undefined && (
        <span className="text-[9px] font-medium normal-case tracking-normal opacity-60">
          {binding}
        </span>
      )}
    </button>
  );
}

export interface ActionButtonsProps {
  onSplit: () => void;
  onEject: () => void;
  onPowerUp: () => void;
  /** The glyph in the selected slot, or null when we are carrying nothing at all. */
  powerUpGlyph: string | null;
  /**
   * The inventory, as rows — see `hud/inventory-strip.ts`. Empty is a player carrying nothing.
   *
   * The slots are `ActionButton`s in this row rather than a control of their own, which is what
   * keeps "a thing you press in the arena" one component: the same pointer discipline, the same
   * `pointer-events-auto` opt-in, the same instant pressed state.
   */
  slots?: readonly InventorySlotView[];
  /** Point at a slot. One edge through the same `ActionQueue` every other press here ends at. */
  onSelectSlot?: (index: number) => void;
  /**
   * Whether the screen is short of room — a phone in landscape. See `hud/layout.ts`.
   *
   * It is the *only* thing this row reads about the surface: one rung down the size ladder for
   * all three controls, so an 80px split does not spend a fifth of a 390-pixel screen. It is a
   * prop rather than a media query of its own because the row and the corner it sits in have to
   * agree about which layout they are drawing.
   */
  compact?: boolean;
  /**
   * The sayings picker — its own `ActionButton` plus the dialog behind it — as a **slot**.
   *
   * A node rather than a handler because the picker has to read the player's own list, which is a
   * tRPC query, and nothing under `game/` knows tRPC exists: `routes/play.tsx` builds it and
   * `GameCanvas` hands it down, exactly as `HudProps.champions` and `DeathScreenProps.standing`
   * are handed down. Absent is the row this game had before sayings.
   */
  sayings?: ReactNode;
}

/**
 * Split, eject and fire, in the bottom-right corner where a right thumb already rests.
 *
 * Split is the largest of the three and sits nearest the corner: it is the action a player
 * reaches for under pressure, and the one whose repeat *is* the mechanic. Eject is smaller and
 * further in, because a mis-hit eject costs mass for nothing. The power-up is furthest in and
 * inert until there is something in the slot, so the thumb that is aiming for a split cannot
 * clip it and lose the orb they just crossed the map for.
 *
 * **The row no longer positions itself.** It is a plain flex row inside the HUD's `bottom-right`
 * zone (`hud/layout.ts`), which is what puts the corner's offsets — and the cap that keeps the
 * row out of the middle of the screen — in the one place the other four zones are decided.
 *
 * On a compact surface every control drops a rung and the keyboard bindings come off the faces:
 * a 390-pixel-tall screen cannot spend 80 of them on one button, and a phone has no Space bar
 * for the caption under it to be about.
 */
export function ActionButtons({
  onSplit,
  onEject,
  onPowerUp,
  powerUpGlyph,
  slots = [],
  onSelectSlot,
  compact = false,
  sayings,
}: ActionButtonsProps) {
  return (
    <div
      data-testid="action-buttons"
      className={`flex flex-col items-end ${compact ? 'gap-1' : 'gap-2'}`}
    >
      {/*
       * The inventory, ABOVE the round row rather than beside it, and that is the mobile HUD's
       * arrangement rather than a preference. The bottom-right zone is the one with no width cap
       * (`hud/layout.ts`): a row anchored to the right edge does not shrink its fixed-size
       * children, it overflows them off the *left*, which is straight into the steering zone the
       * whole layout exists to keep empty. Growing upward spends the right rail instead, where the
       * leaderboard is already the panel declared to give way.
       *
       * The SLOTS are drawn only when there is something in them. A switch control with nothing
       * to switch between is a control that lies, and the empty state is already said by the fire
       * button beside it, which wears `—` and says so out loud. The row itself is always here,
       * because Run is on it and a sprint does not need anything to have been picked up.
       */}
      <div
        data-testid="action-secondary"
        className={`flex items-end ${compact ? 'gap-1' : 'gap-2'}`}
      >
        {/*
         * SPRINT LIVES ON THIS ROW RATHER THAN BESIDE SPLIT, AND THAT IS A WIDTH BUDGET RATHER
         * THAN A TASTE. `CONTROL_RAIL_WIDTH` (35%) is what the round row measures against the
         * narrowest landscape phone this layout is for — 667 CSS pixels — and it is already 220 of
         * them: three 48-pixel targets, a 64-pixel Split and the gaps. A fifth control on that row
         * is 272, which is 41% of that phone and reaches back across the edge of `STEERING_ZONE`
         * the whole layout exists to keep empty. The corner's one permitted direction of growth is
         * *up* (see the note below), so up is where it went — and this row was already there.
         *
         * What it cost is two points of `ACTION_ROW_HEIGHT`, because a 48-pixel Run makes this row
         * taller than the 40-pixel slots did. That is the cheapest thing in the layout to spend:
         * the corner has nothing under it, and the thumb stick is in the *other* corner whenever
         * there is one.
         */}
        <SprintButton compact={compact} />
        {slots.length > 0 && onSelectSlot !== undefined && (
          <div
            data-testid="action-slots"
            className={`flex items-end ${compact ? 'gap-1' : 'gap-2'}`}
          >
            {slots.map((slot) => (
              <ActionButton
                key={slot.kind}
                testId={`slot-button-${slot.index}`}
                label={slot.glyph}
                description={slot.description}
                // The readout goes where a keyboard binding goes, because on this surface there is
                // no key to name and the seconds or the charges are what a thumb needs instead —
                // and it is where the cue goes too. It says SENT, never done: the room refuses a
                // switch in silence, so the second line goes back to the readout on a clock rather
                // than on an acknowledgement, and what is drawn as *selected* is only ever the
                // room's own answer. See SLOT_CUE_MS.
                binding={slot.sent ? 'SENT' : slot.readout}
                // A window that is BURNING is the amber warning tone; a budget — and a window
                // nobody has fired yet, which costs nothing to hold or to leave — is the orb's own
                // gold. The strip has to say which kind a slot is *before* the press that ends one,
                // and amber on a waiting window would be a warning about nothing.
                tone={slot.running ? 'warn' : 'power'}
                size={compact ? 'xs' : 'sm'}
                selected={slot.selected}
                // A switch is a position, not a quantity: holding one must not walk the strip.
                repeat={false}
                onPress={() => onSelectSlot(slot.index)}
              />
            ))}
          </div>
        )}
      </div>

      <div className={`flex items-end ${compact ? 'gap-1' : 'gap-3'}`}>
        {/* Furthest from the corner of the four: it is the one control that opens a panel, and a
          thumb aiming for a split under pressure must never be able to clip it. */}
        {sayings}
        <ActionButton
          testId="powerup-button"
          // The glyph the registry gave what we are holding, so the button *is* the inventory slot
          // on a phone rather than a second thing to look at.
          label={powerUpGlyph ?? '—'}
          description={
            powerUpGlyph === null
              ? 'No power-up held — find a gold orb (Q)'
              : 'Fire your power-up (Q)'
          }
          binding={compact ? undefined : 'Q'}
          tone="power"
          size={compact ? 'sm' : 'md'}
          disabled={powerUpGlyph === null}
          onPress={onPowerUp}
        />
        <ActionButton
          testId="eject-button"
          label="Eject"
          description="Eject mass as bait (W)"
          binding={compact ? undefined : 'W'}
          tone="warn"
          size={compact ? 'sm' : 'md'}
          onPress={onEject}
        />
        <ActionButton
          testId="split-button"
          label="Split"
          description="Split your cluster (space, or double-tap yourself)"
          binding={compact ? undefined : 'Space'}
          size={compact ? 'md' : 'lg'}
          onPress={onSplit}
        />
      </div>
    </div>
  );
}

/**
 * SPRINT — the one control in this row that is held rather than pressed.
 *
 * It is another `ActionButton`, which is the whole bargain this file is under: a new control is
 * one more of them with its own label and its own handlers, never a one-off with its own pointer
 * rules. What it needed from the primitive was a third trigger, and `hold` is that — see
 * `ActionButtonTrigger` for why a stranded hold is the failure it is designed against, and
 * `../input/actions.ts` for why a sprint is a level rather than an edge and must never go near
 * `HoldRepeat`.
 *
 * **IT WRITES THE SINGLETON RATHER THAN TAKING A PROP**, which is `Joystick`'s arrangement and
 * for `Joystick`'s reason: the other reader is the Phaser scene, which folds this together with
 * the keyboard on the frame it derives a heading and hands `GameConnection` one answer. Passing a
 * handler down would mean plumbing a React value into the render loop, and letting the connection
 * take the two sources separately would mean the keyboard's per-frame "not held" cancelling the
 * button on every frame.
 *
 * **It is never disabled**, however empty the bar is. The bar is the readout and it marks the
 * floor a sprint may start from (`hud/StaminaBar.tsx`); a button that went inert would be
 * flickering in and out of reach twenty times a second as the bar crossed that mark, which is a
 * worse control than one that is occasionally refused in silence — and the room refuses a sprint
 * in silence for a duellist and a frozen player anyway, neither of which this side can predict.
 */
function SprintButton({ compact }: { compact: boolean }) {
  return (
    <ActionButton
      testId="sprint-button"
      label="Run"
      description="Hold to sprint (shift)"
      binding={compact ? undefined : 'Shift'}
      trigger="hold"
      size={compact ? 'sm' : 'md'}
      onPress={() => sprintControl.press()}
      onRelease={() => sprintControl.release()}
    />
  );
}
